import React, { useState } from 'react';
import { Folder, ChevronDown, Layers, FlaskConical } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';
import MobileArticleCard from './MobileArticleCard';
import DesktopArticleCard from './DesktopArticleCard';
import ShareButton from './ShareButton';
import { AnimatePresence, motion } from 'framer-motion';
import { useUsageCheck } from '../hooks/useUsageCheck';

// Formatera författarsträng så att max 3 visas innan "et al."
const formatAuthors = (authorsString, language) => {
  if (!authorsString) return t(language, 'inbox_unknown_author');
  const authorsArray = authorsString.split(',').map(a => a.trim());
  if (authorsArray.length > 3) {
    return `${authorsArray.slice(0, 3).join(', ')} et al.`;
  }
  return authorsString;
};

// Hjälpfunktion för att visa nivå på provsökningar
const renderLevelBadge = (level, language) => {
  if (!level) return null;
  const levelStyles = {
    expert: 'bg-purple-100 text-purple-700 border-purple-200',
    student: 'bg-blue-100 text-blue-700 border-blue-200',
    allmänhet: 'bg-emerald-100 text-emerald-700 border-emerald-200'
  };
  const style = levelStyles[level.toLowerCase()] || 'bg-slate-100 text-slate-700 border-slate-200';

  return (
    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md border shadow-sm ${style}`}>
      {level}
    </span>
  );
};

const InboxView = ({
  groupedInbox = {},
  markAsRead,
  deleteItem,
  renderAISummary,
  setView,
  saveFromInbox,
  userId
}) => {
  const { language } = useLanguage();
  const [hiddenIds, setHiddenIds] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});

  const { usageStats } = useUsageCheck(userId);

  const handleHide = (id) => setHiddenIds(prev => [...prev, id]);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const visibleGroups = {};
  let totalVisibleItems = 0;
  let totalUnreadItems = 0;

  Object.entries(groupedInbox).forEach(([groupName, items]) => {
    const visibleItems = (items || []).filter(item => !hiddenIds.includes(item.id));

    if (visibleItems.length > 0) {
      // Sortera: Olästa artiklar först
      visibleItems.sort((a, b) => (a.is_read === b.is_read ? 0 : a.is_read ? 1 : -1));

      visibleGroups[groupName] = visibleItems.map(item => {
        const formattedNames = formatAuthors(item.authors || item.author, language);
        return {
          ...item,
          authors: formattedNames,
          author: formattedNames
        };
      });

      totalVisibleItems += visibleItems.length;
      totalUnreadItems += visibleItems.filter(i => !i.is_read).length;
    }
  });

  return (
    <div className="w-full max-w-full pb-20 overflow-x-hidden box-border">
      {/* Header */}
      <div className="border-b border-slate-200 pb-4 md:pb-6 mt-4 md:mt-8 w-full px-1">
        <div className="flex items-baseline gap-3 mb-1 flex-wrap">
          <h2 className="text-2xl md:text-3xl font-serif text-slate-800 italic tracking-tight break-words">
            {t(language, 'inbox_title')}
          </h2>
          {totalVisibleItems > 0 && (
            <div className="flex items-center gap-1.5 mt-1 md:mt-0">
              <span className="bg-slate-100 text-slate-600 text-[10px] md:text-xs font-bold px-2.5 py-0.5 rounded-full border border-slate-200 shadow-sm">
                {totalVisibleItems} {t(language, 'inbox_total')}
              </span>
              {totalUnreadItems > 0 && (
                <span className="bg-blue-50 text-blue-600 text-[10px] md:text-xs font-bold px-2.5 py-0.5 rounded-full border border-blue-100 shadow-sm">
                  {totalUnreadItems}{' '}
                  {totalUnreadItems === 1
                    ? t(language, 'inbox_unread_singular')
                    : t(language, 'inbox_unread_plural')}
                </span>
              )}
            </div>
          )}
        </div>
        <p className="text-slate-500 text-xs md:text-sm mt-1 leading-relaxed break-words">
          {t(language, 'inbox_subtitle')}
        </p>
      </div>

      {/* Grupperade mappar */}
      {totalVisibleItems > 0 ? (
        <div className="space-y-8 mt-8 w-full px-1">
          {Object.entries(visibleGroups).map(([groupName, items]) => {
            const isExpanded = !!expandedGroups[groupName];
            const totalCount = items.length;
            const unreadCount = items.filter(i => !i.is_read).length;
            const isTestGroup = groupName.includes('🧪');

            return (
              <div key={groupName} className="relative w-full block">
                {/* Lager-effekt under kortet när det är stängt */}
                <AnimatePresence>
                  {!isExpanded && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="absolute -bottom-2 left-4 right-4 h-full bg-slate-200/60 rounded-3xl -z-20 border border-slate-200 transition-all duration-300" />
                      <div className="absolute -bottom-1 left-2 right-2 h-full bg-slate-100 rounded-3xl -z-10 border border-slate-200 transition-all duration-300 shadow-sm" />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Knapp för dragspelsgrupp */}
                <button
                  type="button"
                  onClick={() => toggleGroup(groupName)}
                  className={`group relative z-10 w-full flex items-center justify-between p-4 md:p-5 transition-all duration-300 ease-in-out box-border focus:outline-none ${
                    isExpanded
                      ? isTestGroup
                        ? 'bg-purple-50/50 border-b-2 border-purple-200 rounded-t-3xl shadow-sm'
                        : 'bg-white border-b-2 border-indigo-100 rounded-t-3xl shadow-sm'
                      : isTestGroup
                      ? 'bg-purple-50/20 border border-purple-200 rounded-3xl shadow-md hover:-translate-y-0.5 hover:shadow-lg'
                      : 'bg-white border border-slate-200 rounded-3xl shadow-md hover:-translate-y-0.5 hover:shadow-lg'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                      className={`p-2.5 rounded-2xl transition-colors duration-300 flex items-center justify-center ${
                        isTestGroup
                          ? 'bg-purple-100 text-purple-700'
                          : isExpanded
                          ? 'bg-indigo-50 text-indigo-600'
                          : unreadCount > 0
                          ? 'bg-blue-50/80 text-blue-600'
                          : 'bg-slate-50 text-slate-500 group-hover:bg-slate-100'
                      }`}
                    >
                      {isTestGroup ? (
                        <FlaskConical size={22} className="shrink-0" />
                      ) : isExpanded ? (
                        <Folder size={22} className="shrink-0" />
                      ) : (
                        <Layers size={22} className="shrink-0" />
                      )}
                    </div>
                    <div className="flex flex-col min-w-0 text-left">
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-bold truncate text-[16px] md:text-lg min-w-0 transition-colors duration-300 ${
                            isTestGroup
                              ? 'text-purple-950'
                              : isExpanded
                              ? 'text-indigo-950'
                              : 'text-slate-800 group-hover:text-slate-900'
                          }`}
                        >
                          {groupName}
                        </span>
                        {isTestGroup && (
                          <span className="hidden sm:inline-block bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded-md border border-purple-200 shrink-0">
                            Provsökning
                          </span>
                        )}
                      </div>
                      {!isExpanded && (
                        <span className="text-[11px] font-medium mt-0.5 flex items-center gap-1.5 flex-wrap">
                          <span className="text-slate-500">
                            {totalCount} {t(language, 'inbox_total')}
                          </span>
                          {unreadCount > 0 && (
                            <>
                              <span className="text-slate-300">•</span>
                              <span className="text-blue-500">
                                {unreadCount}{' '}
                                {unreadCount === 1
                                  ? t(language, 'inbox_unread_singular')
                                  : t(language, 'inbox_unread_plural')}
                              </span>
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0 ml-2">
                    {unreadCount > 0 && !isExpanded && (
                      <div className="flex items-center justify-center bg-blue-600 text-white text-[11px] font-bold h-6 min-w-[24px] px-2 rounded-full shadow-sm">
                        {unreadCount}
                      </div>
                    )}
                    <div
                      className={`p-1.5 rounded-full transition-colors duration-300 ${
                        isExpanded
                          ? isTestGroup
                            ? 'bg-purple-100 text-purple-600'
                            : 'bg-indigo-50 text-indigo-500'
                          : 'bg-slate-50 text-slate-400 group-hover:bg-slate-200'
                      }`}
                    >
                      <ChevronDown
                        className={`shrink-0 transition-transform duration-500 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                        size={18}
                      />
                    </div>
                  </div>
                </button>

                {/* Artikellista under öppet dragspel */}
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0, y: -10 }}
                      animate={{ height: 'auto', opacity: 1, y: 0 }}
                      exit={{ height: 0, opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                      className={`relative z-0 overflow-hidden w-full box-border rounded-b-3xl border border-t-0 ${
                        isTestGroup
                          ? 'bg-purple-50/30 border-purple-100'
                          : 'bg-slate-50/50 border-indigo-50'
                      }`}
                    >
                      <div className="px-0 py-4 md:px-4 grid grid-cols-1 xl:grid-cols-2 gap-4 w-full box-border pt-4">
                        {items.map(item => (
                          <div key={item.id} className="w-full h-full flex flex-col space-y-2">
                            {/* Topprad: Nivå-badge och Dela-knapp */}
                            <div className="px-3 sm:px-1 flex items-center justify-between gap-2">
                              {item.level ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-[11px] text-slate-400 font-medium">Nivå:</span>
                                  {renderLevelBadge(item.level, language)}
                                </div>
                              ) : <div />}

                              <ShareButton
                                title={item.article_title || item.title}
                                summary={item.ai_summary || item.summary}
                                url={`${window.location.origin}/share/${item.id}`}
                                language={language}
                              />
                            </div>

                            {/* Mobilkort */}
                            <div className="xl:hidden w-full h-full flex flex-col">
                              <MobileArticleCard
                                item={item}
                                markAsRead={markAsRead}
                                deleteItem={deleteItem}
                                renderAISummary={renderAISummary}
                                saveFromInbox={saveFromInbox}
                                onHide={handleHide}
                                canGenerateSummary={usageStats?.canGenerateSummary}
                                usageStats={usageStats}
                              />
                            </div>

                            {/* Desktopkort */}
                            <div className="hidden xl:flex w-full h-full flex-col">
                              <DesktopArticleCard
                                item={item}
                                markAsRead={markAsRead}
                                deleteItem={deleteItem}
                                renderAISummary={renderAISummary}
                                saveFromInbox={saveFromInbox}
                                onHide={handleHide}
                                canGenerateSummary={usageStats?.canGenerateSummary}
                                usageStats={usageStats}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      ) : (
        /* Tom vy */
        <div className="text-center py-20 bg-white rounded-3xl border border-slate-200 shadow-sm mt-8">
          <div className="text-6xl mb-4">☕</div>
          <h3 className="text-xl font-bold text-slate-800">
            {t(language, 'inbox_empty_title')}
          </h3>
          <button
            type="button"
            onClick={() => setView('topics')}
            className="mt-6 bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-md active:scale-95"
          >
            {t(language, 'inbox_btn_new')}
          </button>
        </div>
      )}
    </div>
  );
};

export default InboxView;